import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getAllBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadAllBrands from "@insite/client-framework/Store/Pages/Brands/Handlers/LoadAllBrands";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useEmblaCarousel from "embla-carousel-react";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    brandsToDisplay = "brandsToDisplay",
    brandsSelected = "brandsSelected",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    maxNumberOfColumns = "maxNumberOfColumns",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.subtitle]: string;
        [fields.brandsToDisplay]: "all" | "select-brands";
        [fields.brandsSelected]: string[];
        [fields.numberOfBrandsToDisplay]: number;
        [fields.maxNumberOfColumns]: number;
        [fields.customCSS]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    allBrandsDataView: getAllBrandsDataView(state),
});

const mapDispatchToProps = {
    loadAllBrands,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface BrandsSliderStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    subtitleText?: TypographyPresentationProps;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    brandLink?: ClickablePresentationProps;
    brandImage?: LazyImageProps;
}

export const brandsSliderStyles: BrandsSliderStyles = {
    wrapper: {
        css: css`
            padding: 40px 0;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-weight: 700;
            font-size: 24px;
            margin-bottom: 8px;
        `,
    },
    subtitleText: {
        css: css`
            text-align: center;
            font-size: 15px;
            max-width: 720px;
            margin: 0 auto 36px auto;
        `,
    },
    mainContainer: {
        gap: 0,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            flex-basis: 4%;
            max-width: 4%;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: transparent;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            flex-basis: 4%;
            max-width: 4%;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            background: transparent;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            flex-basis: 92%;
            max-width: 92%;
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            align-items: center;
            padding: 14px 0;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.3s ease;
        `,
    },
    brandLink: {
        css: css`
            width: 100%;
            height: 90px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    brandImage: {
        css: css`
            margin: auto;
            max-width: 100%;
            max-height: 90px;
            height: auto;
            width: auto;
        `,
    },
};

const styles = brandsSliderStyles;

/**
 * Custom brand logo slider per the SL31 Industries designs.
 * Displays selected brand logos in the configured order, each linking to the brand detail page.
 */
const BrandsSlider = ({ fields, allBrandsDataView, loadAllBrands, theme }: Props) => {
    useEffect(() => {
        if (!allBrandsDataView.value && !allBrandsDataView.isLoading) {
            loadAllBrands();
        }
    }, [allBrandsDataView.value, allBrandsDataView.isLoading]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const [centeredIndex, setCenteredIndex] = useState(-1);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const updateCenteredSlide = () => {
        if (!embla) {
            return;
        }
        const slidesInView = embla.slidesInView();
        if (slidesInView.length > 0) {
            setCenteredIndex(slidesInView[Math.floor(slidesInView.length / 2)]);
        }
    };

    const handleCarouselKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            embla && embla.scrollPrev();
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            embla && embla.scrollNext();
        }
    };

    const getSlidesToShow = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 7;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToShow: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[1]) {
            localSlidesToShow = 2;
        } else if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToShow = 3;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToShow = 5;
        } else {
            localSlidesToShow = maxNumberOfColumns;
        }
        return Math.min(localSlidesToShow, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToShow, setSlidesToShow] = useState(0);

    useEffect(() => {
        setSlidesToShow(getSlidesToShow());
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    const brandList = allBrandsDataView.value || [];

    let brands: BrandModel[];
    if (fields.brandsToDisplay === "select-brands" && fields.brandsSelected && fields.brandsSelected.length > 0) {
        brands = [];
        fields.brandsSelected.forEach(brandId => {
            const brand = brandList.find(b => b.id === brandId);
            if (brand) {
                brands.push(brand);
            }
        });
    } else {
        brands = brandList.slice(0, fields.numberOfBrandsToDisplay || 18);
    }

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
        embla.on("select", updateCenteredSlide);
        embla.on("settle", updateCenteredSlide);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    useEffect(() => {
        if (!embla || brands.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            // show a group of logos at once, but arrows advance a single logo at a time
            slidesToScroll: 1,
            watchDrag: isTouchDevice(),
            loop: brands.length > slidesToShow,
        });
        setCanScroll();
        updateCenteredSlide();
    }, [embla, slidesToShow, brands.length]);

    if (brands.length === 0) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${fields.customCSS}
        `,
    };

    const showArrows = brands.length > slidesToShow;

    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="brandsSlider">
            {fields.title && <Typography {...styles.titleText}>{fields.title}</Typography>}
            {fields.subtitle && (
                <Typography as="p" {...styles.subtitleText}>
                    {fields.subtitle}
                </Typography>
            )}
            <GridContainer {...styles.mainContainer}>
                <GridItem {...styles.prevArrowGridItem}>
                    {showArrows && (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="brandsSliderPrevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    )}
                </GridItem>
                <GridItem {...styles.carouselGridItem}>
                    <StyledWrapper
                        {...styles.carouselWrapper}
                        tabIndex={0}
                        role="region"
                        aria-label={translate("Brands slider")}
                        onKeyDown={handleCarouselKeyDown}
                    >
                        <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                            <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                {brands.map((brand, index) => (
                                    <StyledWrapper
                                        {...styles.carouselSlide}
                                        key={brand.id}
                                        style={{ width: `calc(100% / ${slidesToShow})` }}
                                    >
                                        <StyledWrapper
                                            {...styles.carouselSlideInner}
                                            style={{
                                                transform: index === centeredIndex ? "scale(1.2)" : "scale(1)",
                                            }}
                                        >
                                            <Clickable
                                                href={brand.detailPagePath}
                                                {...styles.brandLink}
                                                data-test-selector={`brandsSliderBrand_${brand.id}`}
                                            >
                                                <LazyImage
                                                    src={brand.logoSmallImagePath}
                                                    altText={
                                                        brand.logoAltText
                                                            ? `${translate("Go to brand page")} ${brand.logoAltText}`
                                                            : `${translate("Go to brand page")} ${brand.name}`
                                                    }
                                                    {...styles.brandImage}
                                                />
                                            </Clickable>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                ))}
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.nextArrowGridItem}>
                    {showArrows && (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="brandsSliderNextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    )}
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(BrandsSlider))),
    definition: {
        group: "Brands",
        icon: "gallery-thumbnails",
        displayName: "Brands Slider",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Trusted Brands",
                fieldType: "Translatable",
                sortOrder: 0,
                tab: basicTab,
            },
            {
                name: fields.subtitle,
                displayName: "Subtitle",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 1,
                tab: basicTab,
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    { displayName: "Select brands", value: "select-brands" },
                    { displayName: "All brands", value: "all" },
                ],
                defaultValue: "select-brands",
                fieldType: "General",
                sortOrder: 2,
                tab: basicTab,
                tooltip: "Selected brands display in the order they are chosen.",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 3,
                tab: basicTab,
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "IntegerField",
                defaultValue: 18,
                min: 1,
                max: 36,
                fieldType: "General",
                sortOrder: 4,
                tab: basicTab,
                isVisible: item => item.fields.brandsToDisplay === "all",
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Logos Visible at Once",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "5", value: 5 },
                    { displayName: "6", value: 6 },
                    { displayName: "7", value: 7 },
                    { displayName: "8", value: 8 },
                ],
                hideEmptyOption: true,
                defaultValue: 7,
                fieldType: "General",
                sortOrder: 5,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: "",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            },
        ],
    },
};

export default widgetModule;
